import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Avatar, Switch, withStyles } from '@material-ui/core';
import { VolumeUp } from '@material-ui/icons';
import classes from './RealPlay.module.scss';
import NewDatePicker from 'features/realplays/Components/Common/NewDatePicker';
import { InputSelect, InputText, TextArea } from 'shared_ui_components';
import { useGetListUsersRepsV2Query } from 'features/roleplay/Roleplay.queries';
import User from 'infrastructure/User';
import RoleplayUserOption from '../AsignRoleplay/RoleplayUserOption';
import { components } from 'react-select';
import { buyingContext } from './AssignRealPlayContants';
import AssignRealPlayDataHelper from 'features/realplays/AssignRealPlayData.helper';
import { useTimezoneStore } from 'infrastructure/useTimeZoneStore';
import NewDateTime from 'infrastructure/NewDateTime';
import { useRoleplayStore } from 'features/roleplay/Roleplay.store';
import { useRealplayStore } from 'features/realplays/RealPlays.store';
import Loading from 'shared_ui_components/Loading';

const CustomSwitch = withStyles((theme) => ({
  root: {
    width: 34,
    height: 18,
    padding: '0 0 2px',
    display: 'flex',
  },
  switchBase: {
    padding: 2,
    color: theme.palette.grey[500],
    '&$checked': {
      transform: 'translateX(16px)',
      color: theme.palette.common.white,
      '& + $track': {
        opacity: 1,
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
      },
    },
  },
  thumb: {
    width: 14,
    height: 14,
    boxShadow: 'none',
  },
  checked: {},
  track: {
    border: `1px solid ${theme.palette.grey[500]}`,
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: theme.palette.common.white,
  },
}))(Switch);

const RoleplayBodyDetails = ({ isLoadingAttitudes, isLoadingCoach, publicOption }) => {
  const { data: usersListRep = [], isLoading } = useGetListUsersRepsV2Query();
  const [valueList, setValueList] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const { timezone } = useTimezoneStore();
  const previousTimezone = useRef(timezone);

  const {
    isRep,
    personas_ai,
    attitudes,
    deadline,
    context,
    setDeadLine,
    updateAttitudesSelection,
    updatePersonaSelection,
    updateUsersSelection,
    updateIndustrySelector,
    updateTitleContext,
    updateCustomerSituation,
  } = AssignRealPlayDataHelper();

  const { scenariosList, setScenariosList } = useRoleplayStore();

  const { publicAccess, setPublicAccess } = useRealplayStore();

  const options = useMemo(() => {
    if (!usersListRep?.length > 0) return [];
    return usersListRep?.map((user) => {
      return {
        label: User.getUserName(user?.user),
        value: user?.user?.uuid,
        roleplay: user?.role_plays,
        user: user?.user,
      };
    });
  }, [usersListRep]);

  const industries = useMemo(() => {
    return buyingContext?.map((item) => ({
      label: item?.industry,
      value: item?.industry,
      description: item?.description,
    }));
  }, []);

  useEffect(() => {
    if (previousTimezone.current !== timezone && deadline) {
      previousTimezone.current = timezone;

      try {
        const currentDate = new Date(deadline);
        if (!isNaN(currentDate)) {
          const newDeadline = NewDateTime.formatDate(currentDate);
          setDeadLine(newDeadline);
        }
      } catch (error) {
        console.error('Error updating deadline on timezone change:', error);
      }
    }
  }, [timezone, deadline, setDeadLine]);

  const handleSelectPersona = (uuid) => updatePersonaSelection(uuid);
  const handleSelect = (id) => updateAttitudesSelection(id);

  const handleAcceptDate = (query) => {
    setDeadLine(query);
  };

  const handleChangeTitle = (value) => updateTitleContext(value);
  const handleChangeTextArea = (text) => updateCustomerSituation(text);
  const handleChangeIndustry = (option) =>
    updateIndustrySelector(option?.value, option?.description);

  const handleChangeReps = (values) => {
    let newUsers = values;
    if (values?.some((item) => item.value === -1)) {
      newUsers = usersListRep?.map((user) => {
        return {
          label: User.getUserName(user?.user),
          value: user?.user?.uuid,
          roleplay: user?.role_plays,
          user: user?.user,
        };
      });
    }
    setValueList(newUsers);
    updateUsersSelection(newUsers);
  };

  const handleChange = (value, scenario_) => {
    const index = scenarios.findIndex((scenario) => scenario?.uuid === scenario_.uuid);
    scenarios[index].isSelected = value;
    setScenariosList([...scenariosList]);
  };

  const handlePublicOption = (event) => setPublicAccess(event.target.checked);

  useEffect(() => {
    setScenarios(scenariosList);
  }, [scenariosList]);

  useEffect(() => {
    if (buyingContext?.length > 0) {
      updateIndustrySelector(buyingContext[0].industry, buyingContext[0].description);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classes.detailsContainer}>
      {isRep && publicOption && (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
          <span>Enable public access</span>
          <CustomSwitch checked={publicAccess} onChange={handlePublicOption} name="checkedB" />
        </div>
      )}
      <div style={{ border: '1px solid #ECEFF1', borderRadius: '12px', padding: '12px' }}>
        <p className="heading5" style={{ marginBottom: '0.7rem', color: '#193448' }}>
          Step 1: Select your AI prospect
        </p>
        <p className={classes.deadlineTitle}>Your AI prospect </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2,1fr)',
            gap: '0.5rem',
          }}
        >
          {isLoadingCoach ? (
            <Loading loading circular margin={100} width="100%" />
          ) : (
            (personas_ai ?? []).map((i, index) => {
              return (
                <div
                  className={classes.coach}
                  key={`persona-${index}`}
                  style={
                    i?.isSelected ? { backgroundColor: '#E9F6FF' } : { border: '1px solid #E7F0F5' }
                  }
                  onClick={() => handleSelectPersona(i?.uuid)}
                >
                  <Avatar
                    alt="img_persona"
                    src={i?.profile_pic}
                    style={{ width: '48px', height: '48px' }}
                  />
                  <div className={classes.details}>
                    <div>
                      <VolumeUp fontSize="small" />
                      <p>{i?.name}</p>
                    </div>
                    <p style={{ color: '#607d8b' }}>{i?.description ?? 'By SalesTable'}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div style={{ marginTop: '0.3rem' }}>
          <p className={classes.deadlineTitle}>Attitude</p>
          <div className={classes.attitude}>
            {isLoadingAttitudes ? (
              <Loading loading circular margin={100} width="100%" />
            ) : (
              (attitudes ?? []).map((i, index) => {
                return (
                  <div
                    key={`att-${index}`}
                    style={
                      i?.isSelected
                        ? {
                            backgroundColor: '#E9F6FF',
                            color: '#1F99FE',
                            fontWeight: 600,
                          }
                        : {
                            border: '1px solid #E7F0F5',
                            fontWeight: 400,
                            color: '#193448',
                          }
                    }
                    onClick={() => handleSelect(i?.uuid)}
                  >
                    {i?.name}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div style={{ border: '1px solid #ECEFF1', borderRadius: '12px', padding: '12px' }}>
        <p className="heading5" style={{ marginBottom: '0.7rem', color: '#193448' }}>
          Step 2: Set the buying context
        </p>

        <InputSelect
          label={
            <div style={{ display: 'flex', gap: '0.2rem' }}>
              <p>Industry</p>
              <p style={{ color: 'red' }}>*</p>
            </div>
          }
          placeholder="Select industry"
          onChange={(value) => handleChangeIndustry(value)}
          options={industries}
          value={industries.find((opt) => opt?.value === context?.industry)}
          closeMenuOnSelect={true}
          noOptionsMessage={() => 'No matches found'}
          components={{ Option: CustomOption }}
        />

        <div>
          <div style={{ display: 'flex', gap: '0.2rem' }}>
            <p className={classes.deadlineTitle}>Role or Title</p>
            <p style={{ color: 'red' }}>*</p>
          </div>
          <InputText
            id="goalTarget"
            placeholder="e.g. VP of Sales"
            value={context?.title}
            onChange={(e) => handleChangeTitle(e.target.value)}
          />
        </div>
        <div>
          <div style={{ display: 'flex', gap: '0.2rem' }}>
            <p className={classes.deadlineTitle}>Customer Situation</p>
            <p style={{ color: 'red' }}>*</p>
          </div>
          <TextArea
            rows={4}
            placeholder="Enter new roleplay's description"
            value={context?.customer_situation}
            onChange={(e) => handleChangeTextArea(e.target.value)}
          />
        </div>
      </div>

      {scenarios && scenarios.length > 0 && (
        <div style={{ border: '1px solid #ECEFF1', borderRadius: '12px', padding: '12px' }}>
          <div style={{ display: 'flex', gap: '0.2rem' }}>
            <p className="heading5" style={{ marginBottom: '0.7rem', color: '#193448' }}>
              Step 3: Select objections
            </p>
            <p style={{ color: 'red' }}>*</p>
          </div>

          <p className={classes.deadlineTitle}>Scenarios</p>
          <div className={classes.attitude} style={{ marginLeft: '0.5rem' }}>
            {(scenarios ?? []).map((sce, index) => {
              return (
                <div
                  key={`sce-${index}`}
                  style={
                    sce?.isSelected
                      ? {
                          backgroundColor: '#E9F6FF',
                          color: '#1F99FE',
                          fontWeight: 600,
                        }
                      : {
                          border: '1px solid #E7F0F5',
                          fontWeight: 400,
                          color: '#193448',
                        }
                  }
                  onClick={() => handleChange(!sce?.isSelected, sce)}
                >
                  {sce?.name}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!isRep && (
        <div className={classes.selectAssignWrapper}>
          <InputSelect
            disabled={isLoading}
            isMulti
            closeMenuOnSelect={true}
            options={[
              {
                value: -1,
                label:
                  options?.length === 0 || options?.length === valueList?.length
                    ? 'There are no more sales reps to select'
                    : 'All sales reps',
              },
              ...options,
            ]}
            label="Sales Reps"
            placeholder={isLoading ? 'Loading...' : 'Select Sales Reps'}
            onChange={(values) => handleChangeReps(values)}
            noOptionsMessage={() => 'No sales reps found'}
            isClearable
            value={valueList}
            components={{ Option: RoleplayUserOption }}
          />
        </div>
      )}

      <div style={{ padding: '5px 12px' }}>
        <p className={classes.deadlineTitle}>Deadline</p>
        <NewDatePicker
          InputLabel={'Deadline'}
          handleApply={(date) => handleAcceptDate(date)}
          defaultFilter={{
            date: deadline,
          }}
        />
      </div>
    </div>
  );
};
const CustomOption = ({ innerRef, innerProps, ...rest }) => {
  return (
    <div ref={innerRef} {...innerProps}>
      <components.Option {...rest}>
        <div className={classes.customRoleplayOpt}>
          <p>{rest?.data?.label}</p>
        </div>
      </components.Option>
    </div>
  );
};

export default RoleplayBodyDetails;
